import React from 'react';
import { Button, Col, Form, Input, Row, Space, Switch } from 'antd';
import { CloseOutlined, EditOutlined, SaveOutlined, UserOutlined } from '@ant-design/icons';

import SectionCard from '../../../../components/shared/SectionCard';
import { Patient } from '../../../../types/healthcare/PatientTypes';
import {
	FAMILY_NAME_LABEL,
	GIVEN_NAME_LABEL,
	LOCKED_NAME_INPUT_CLASS,
	MIDDLE_NAME_LABEL,
	ONE_NAME_ONLY_LABEL,
	PatientPrimaryNameDraft,
	patientPrimaryNameDraftFromPatient,
} from './patientNameDisplay';

export const PRIMARY_NAME_SECTION_TITLE = 'Primary Name';

/**
 * The host-owned explicit edit session for an existing patient's canonical
 * primary name. `draft` is null while the section is locked.
 */
export interface PatientPrimaryNameEditControls {
	draft: PatientPrimaryNameDraft | null;
	isSaving: boolean;
	/**
	 * CU-86d439zzf: another canonical write is unresolved, so a commit cannot
	 * start yet. The draft stays editable — it is isolated from the record state
	 * that write carries — only the Save action waits.
	 */
	isBlocked?: boolean;
	onEdit: () => void;
	onChange: (patch: Partial<PatientPrimaryNameDraft>) => void;
	onOneNameOnlyChange: (checked: boolean) => void;
	onCancel: () => void;
	onSave: () => void;
}

interface PatientPrimaryNameSectionProps {
	patient: Patient;
	/** Direct-mode component setters, used for a not-yet-created patient and for
	 *  hosts that supply no explicit edit session. */
	onFieldChange: (field: 'first_name' | 'middle_name' | 'last_name', value: string) => void;
	onOneNameOnlyChange: (checked: boolean) => void;
	onFieldBlur: () => void;
	edit?: PatientPrimaryNameEditControls;
}

/**
 * B1.4 (HI_005_005804 / HI_010_005804 / HI_015_005804 / HI_015_005824) — the
 * four controls that make up the canonical primary name, kept together as one
 * explicitly edited group.
 *
 * CU-86d439zzf: for an EXISTING patient with an edit session the controls are
 * locked until Edit is selected, and the draft becomes authoritative only
 * through the section's Save or the modal's own Save. A not-yet-created patient
 * keeps the ordinary editable create behaviour, since it has no previous
 * authoritative name to preserve.
 */
const PatientPrimaryNameSection: React.FC<PatientPrimaryNameSectionProps> = ({
	patient,
	onFieldChange,
	onOneNameOnlyChange,
	onFieldBlur,
	edit,
}) => {
	const isExistingPatient = !!patient.id;
	const session = isExistingPatient ? edit : undefined;
	const isEditing = !!session?.draft;
	const isLocked = !!session && !isEditing;
	/*
	 * CU-86d439zzf (PR #234 review): the submitted tuple is snapshotted when the
	 * request begins, so the whole group is frozen until it resolves — a change
	 * accepted here would be silently lost when success closes the session.
	 */
	const isCommitting = isEditing && !!session?.isSaving;

	const values: PatientPrimaryNameDraft =
		session?.draft ?? patientPrimaryNameDraftFromPatient(patient);
	const isOneNameOnly = values.has_one_name_only === true;

	const setField = (field: 'first_name' | 'middle_name' | 'last_name', value: string) => {
		if (session && isEditing) {
			session.onChange({ [field]: value } as Partial<PatientPrimaryNameDraft>);
			return;
		}
		onFieldChange(field, value);
	};

	const setOneNameOnly = (checked: boolean) => {
		if (session && isEditing) {
			session.onOneNameOnlyChange(checked);
			return;
		}
		onOneNameOnlyChange(checked);
	};

	const handleBlur = () => {
		if (session) return;
		onFieldBlur();
	};

	const groupDisabled = isLocked || isCommitting;
	const givenDisabled = groupDisabled || isOneNameOnly;
	const middleDisabled = givenDisabled;
	const mutedIf = (disabled: boolean) => (disabled ? LOCKED_NAME_INPUT_CLASS : undefined);

	const fi = { marginBottom: 8 };

	const actions = session ? (
		isEditing ? (
			<Space size={6}>
				<Button size="small" icon={<CloseOutlined />} onClick={session.onCancel} disabled={session.isSaving}>
					Cancel
				</Button>
				<Button
					size="small"
					type="primary"
					icon={<SaveOutlined />}
					onClick={session.onSave}
					loading={session.isSaving}
					disabled={!!session.isBlocked}
				>
					Save
				</Button>
			</Space>
		) : (
			<Button size="small" icon={<EditOutlined />} onClick={session.onEdit}>
				Edit
			</Button>
		)
	) : undefined;

	return (
		<SectionCard title={PRIMARY_NAME_SECTION_TITLE} extra={actions}>
			<Form layout="vertical" size="small">
				<Row gutter={[8, 0]}>
					<Col xs={24} md={6}>
						<Form.Item label={ONE_NAME_ONLY_LABEL} style={fi}>
							<div>
								<Switch
									aria-label={ONE_NAME_ONLY_LABEL}
									checked={isOneNameOnly}
									disabled={groupDisabled}
									onChange={setOneNameOnly}
								/>
							</div>
						</Form.Item>
					</Col>
					<Col xs={24} md={6}>
						<Form.Item label={GIVEN_NAME_LABEL} style={fi}>
							<Input
								aria-label={GIVEN_NAME_LABEL}
								className={mutedIf(givenDisabled)}
								disabled={givenDisabled}
								value={isOneNameOnly ? '' : values.first_name}
								onChange={(e) => setField('first_name', e.target.value)}
								onBlur={handleBlur}
								placeholder="Given name"
								prefix={<UserOutlined />}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={6}>
						<Form.Item label={MIDDLE_NAME_LABEL} style={fi}>
							<Input
								aria-label={MIDDLE_NAME_LABEL}
								className={mutedIf(middleDisabled)}
								disabled={middleDisabled}
								value={isOneNameOnly ? '' : values.middle_name}
								onChange={(e) => setField('middle_name', e.target.value)}
								onBlur={handleBlur}
								placeholder="Middle name (optional)"
								prefix={<UserOutlined />}
							/>
						</Form.Item>
					</Col>
					<Col xs={24} md={6}>
						<Form.Item label={FAMILY_NAME_LABEL} required style={fi}>
							<Input
								aria-label={FAMILY_NAME_LABEL}
								className={mutedIf(groupDisabled)}
								disabled={groupDisabled}
								value={values.last_name}
								onChange={(e) => setField('last_name', e.target.value)}
								onBlur={handleBlur}
								placeholder={isOneNameOnly ? "The patient's single recorded name" : 'Family name'}
								prefix={<UserOutlined />}
							/>
						</Form.Item>
					</Col>
				</Row>
			</Form>
		</SectionCard>
	);
};

export default PatientPrimaryNameSection;
